import { Decimal } from "decimal.js";
import { z } from "zod";

/**
 * Monetary amount, arbitrary precision. All platform money flows through this type —
 * DB reads, domain computation, event serialization — with conversion to number or
 * formatted string only at the display boundary.
 *
 * `Money` is a plain alias for `Decimal` rather than a branded nominal type so that
 * arithmetic methods (`a.plus(b)`) don't force round-trip casts through a constructor.
 * Discipline comes from consistent use of `Money` in type signatures.
 */
export type Money = Decimal;
export type Percentage = Decimal;

export type MoneyInput = Decimal | string | number;

export const ZERO_MONEY: Money = new Decimal(0);

/**
 * Construct a Money value. Accepts decimal strings (preferred for DB reads),
 * existing Decimals (pass-through), or numbers (for literal test/demo data).
 */
export function money(value: MoneyInput): Money {
  return value instanceof Decimal ? value : new Decimal(value);
}

/**
 * Construct a Percentage in the [0, 100] domain. Values outside the range throw.
 */
export function percentage(value: MoneyInput): Percentage {
  const p = value instanceof Decimal ? value : new Decimal(value);
  if (p.lt(0) || p.gt(100)) {
    throw new RangeError(`Percentage out of range [0, 100]: ${p.toString()}`);
  }
  return p;
}

export function moneyToString(value: Money): string {
  return value.toFixed();
}

export function moneyToStringOrNull(value: Money | null): string | null {
  return value === null ? null : value.toFixed();
}

export function moneyFromNullable(value: string | null): Money | null {
  return value === null ? null : new Decimal(value);
}

export { Decimal };

export const moneySchema = z
  .union([z.instanceof(Decimal), z.string(), z.number()])
  .transform((v) => (v instanceof Decimal ? v : new Decimal(v)));

export const percentageSchema = z
  .union([z.instanceof(Decimal), z.string(), z.number()])
  .transform((v, ctx) => {
    const p = v instanceof Decimal ? v : new Decimal(v);
    if (p.lt(0) || p.gt(100)) {
      ctx.addIssue({
        code: "custom",
        message: `Percentage out of range [0, 100]: ${p.toString()}`,
      });
      return z.NEVER;
    }
    return p;
  });
